import { useMemo, useState } from 'react';
import { isGuarded } from '../../api/dashboards';
import type { AnalysisResult, BlockResult, DashboardBlock as Block } from '../../api/dashboards';
import { StatTile } from '../charts';
import { BoxPlot, type BoxDatum } from '../charts/BoxPlot';
import { Heatmap, type HeatmapCell } from '../charts/Heatmap';
import { Plot } from '../charts/Plot';
import { chartInk } from '../../design-system/tokens';
import { axis, baseLayout, seriesColor } from '../charts/plotTheme';
import { Alert } from '../ui';
import { formatCell } from './format';
import { ResultTable } from './ResultTable';
import './DashboardBlock.css';

function labelOf(result: AnalysisResult, field: string): string {
  return result.columns.find((column) => column.name === field)?.label ?? field;
}

function typeOf(result: AnalysisResult, field: string): string {
  return result.columns.find((column) => column.name === field)?.type ?? 'text';
}

function categories(result: AnalysisResult, field: string): string[] {
  return result.rows.map((row) => formatCell(row[field], typeOf(result, field)));
}

function numbers(result: AnalysisResult, field: string): (number | null)[] {
  return result.rows.map((row) => {
    const value = row[field];
    return typeof value === 'number' ? value : null;
  });
}

/** Rows split into one series per value of `seriesField`. */
function grouped(result: AnalysisResult, xField: string, seriesField: string, valueField: string) {
  const names: string[] = [];
  const byName = new Map<string, { x: string[]; y: (number | null)[] }>();

  result.rows.forEach((row) => {
    const name = formatCell(row[seriesField], typeOf(result, seriesField));
    let series = byName.get(name);
    if (!series) {
      series = { x: [], y: [] };
      byName.set(name, series);
      names.push(name);
    }
    series.x.push(formatCell(row[xField], typeOf(result, xField)));
    const value = row[valueField];
    series.y.push(typeof value === 'number' ? value : null);
  });

  return names.map((name) => ({ name, ...(byName.get(name) ?? { x: [], y: [] }) }));
}

/** What the figure holds, in words, under every chart. */
function reading(type: Block['type'], rows: number, series: number): string {
  if (series > 1) return `${series} series over ${rows.toLocaleString()} rows.`;
  const unit =
    type === 'bar' || type === 'histogram'
      ? 'bar'
      : type === 'pie' || type === 'donut'
        ? 'slice'
        : 'point';
  return `${rows.toLocaleString()} ${unit}${rows === 1 ? '' : 's'}.`;
}

function KpiBlock({ block, result }: { block: Block; result: AnalysisResult }) {
  const field = block.display.value_field ?? result.columns[0]?.name ?? '';
  const secondField = block.display.secondary_field ?? '';
  const row = result.rows[0];
  const value = row ? formatCell(row[field], typeOf(result, field)) : '—';

  const detail = secondField
    ? `${labelOf(result, secondField)}: ${row ? formatCell(row[secondField], typeOf(result, secondField)) : '—'}`
    : block.description || undefined;

  return <StatTile label={block.title} value={value} detail={detail} />;
}

function CartesianBlock({ block, result }: { block: Block; result: AnalysisResult }) {
  const xField = block.display.x_field ?? '';
  const valueField = (block.display.value_fields ?? [])[0] ?? '';
  const seriesField = block.display.series_field ?? null;

  const { data, layout, seriesCount } = useMemo(() => {
    const isLine = block.type === 'line' || block.type === 'area';
    const series = seriesField
      ? grouped(result, xField, seriesField, valueField)
      : [
          {
            name: labelOf(result, valueField),
            x: categories(result, xField),
            y: numbers(result, valueField),
          },
        ];

    const traces = series.map((entry, index) => ({
      type: isLine ? 'scatter' : 'bar',
      mode: isLine ? 'lines+markers' : undefined,
      fill: block.type === 'area' ? 'tozeroy' : undefined,
      fillcolor: block.type === 'area' ? `${seriesColor(index)}33` : undefined,
      name: entry.name,
      x: entry.x,
      y: entry.y,
      marker: { color: seriesColor(index), size: 8 },
      line: isLine ? { color: seriesColor(index), width: 2 } : undefined,
      hovertemplate: `%{x}<br>${entry.name}: %{y:,}<extra></extra>`,
    }));

    return {
      data: traces,
      seriesCount: traces.length,
      layout: {
        ...baseLayout({ showLegend: traces.length > 1 }),
        xaxis: axis({
          title: block.display.x_label ?? undefined,
          showGrid: false,
          type: isLine ? undefined : ('category' as const),
        }),
        yaxis: axis({ title: labelOf(result, valueField) }),
        bargap: block.type === 'histogram' ? 0.05 : 0.3,
      },
    };
  }, [block, result, seriesField, valueField, xField]);

  return (
    <>
      <Plot data={data} layout={layout} label={block.title} />
      <p className="sc-block__reading">{reading(block.type, result.row_count, seriesCount)}</p>
    </>
  );
}

function PieBlock({ block, result }: { block: Block; result: AnalysisResult }) {
  const xField = block.display.x_field ?? '';
  const valueField = (block.display.value_fields ?? [])[0] ?? '';

  const { data, layout } = useMemo(() => {
    const labels = categories(result, xField);
    return {
      data: [
        {
          type: 'pie',
          hole: block.type === 'donut' ? 0.55 : 0,
          labels,
          values: numbers(result, valueField),
          marker: {
            colors: labels.map((_, index) => seriesColor(index)),
            // The 2px surface ring the design system asks for between
            // touching marks — the token, not a hex (Rule 16).
            line: { color: chartInk.surface, width: 2 },
          },
          textinfo: 'label+percent',
          hovertemplate: `%{label}<br>${labelOf(result, valueField)}: %{value:,}<extra></extra>`,
          sort: false,
        },
      ],
      layout: { ...baseLayout({ showLegend: false }) },
    };
  }, [block.type, result, valueField, xField]);

  return (
    <>
      <Plot data={data} layout={layout} label={block.title} />
      <p className="sc-block__reading">{reading(block.type, result.row_count, 1)}</p>
    </>
  );
}

function ScatterBlock({ block, result }: { block: Block; result: AnalysisResult }) {
  const xField = block.display.x_field ?? '';
  const yField = block.display.y_field ?? '';
  const labelField = block.display.series_field ?? '';

  const { data, layout } = useMemo(
    () => ({
      data: [
        {
          type: 'scatter',
          mode: 'markers',
          x: numbers(result, xField),
          y: numbers(result, yField),
          text: labelField ? categories(result, labelField) : undefined,
          marker: { color: seriesColor(0), size: 10 },
          hovertemplate: labelField
            ? `%{text}<br>${labelOf(result, xField)}: %{x:,}<br>${labelOf(result, yField)}: %{y:,}<extra></extra>`
            : `${labelOf(result, xField)}: %{x:,}<br>${labelOf(result, yField)}: %{y:,}<extra></extra>`,
        },
      ],
      layout: {
        ...baseLayout({ showLegend: false }),
        xaxis: axis({ title: labelOf(result, xField) }),
        yaxis: axis({ title: labelOf(result, yField) }),
      },
    }),
    [labelField, result, xField, yField],
  );

  return (
    <>
      <Plot data={data} layout={layout} label={block.title} />
      <p className="sc-block__reading">{reading('scatter', result.row_count, 1)}</p>
    </>
  );
}

/** The five measures of a box, in the order the plot draws them. */
function boxData(result: AnalysisResult, xField: string): BoxDatum[] {
  const measures = result.columns.filter((column) => column.role === 'metric');
  if (measures.length < 5) return [];
  const [lowest, low, middle, high, highest] = measures;

  return result.rows
    .map((row) => ({
      label: formatCell(row[xField], typeOf(result, xField)),
      lowest: Number(row[lowest?.name ?? ''] ?? 0),
      low: Number(row[low?.name ?? ''] ?? 0),
      middle: Number(row[middle?.name ?? ''] ?? 0),
      high: Number(row[high?.name ?? ''] ?? 0),
      highest: Number(row[highest?.name ?? ''] ?? 0),
    }))
    .filter((entry) => Number.isFinite(entry.middle));
}

function heatmapCells(
  result: AnalysisResult,
  xField: string,
  yField: string,
  valueField: string,
): HeatmapCell[] {
  return result.rows.map((row) => ({
    x: formatCell(row[xField], typeOf(result, xField)),
    y: formatCell(row[yField], typeOf(result, yField)),
    value: typeof row[valueField] === 'number' ? (row[valueField] as number) : null,
  }));
}

function Figure({ block, result }: { block: Block; result: AnalysisResult }) {
  switch (block.type) {
    case 'pie':
    case 'donut':
      return <PieBlock block={block} result={result} />;
    case 'scatter':
      return <ScatterBlock block={block} result={result} />;
    case 'box':
      return (
        <BoxPlot
          data={boxData(result, block.display.x_field ?? '')}
          valueLabel={result.columns.find((column) => column.role === 'metric')?.label ?? ''}
        />
      );
    case 'heatmap':
      return (
        <Heatmap
          cells={heatmapCells(
            result,
            block.display.x_field ?? '',
            block.display.y_field ?? '',
            (block.display.value_fields ?? [])[0] ?? '',
          )}
          xLabel={labelOf(result, block.display.x_field ?? '')}
          yLabel={labelOf(result, block.display.y_field ?? '')}
          valueLabel={labelOf(result, (block.display.value_fields ?? [])[0] ?? '')}
        />
      );
    default:
      return <CartesianBlock block={block} result={result} />;
  }
}

/**
 * One block of a dashboard: the question's answer, drawn.
 *
 * Every chart carries the same numbers as a table underneath it, collapsed by
 * default. A figure is a picture to a screen reader, and the table is also how
 * someone checks what they are being shown rather than taking it on trust.
 */
export function DashboardBlock({
  block,
  state,
  titleAs: Title = 'h2',
}: {
  block: Block;
  state?: BlockResult;
  /**
   * The heading level for the block's title. `h2` on a dashboard's own page,
   * where the dashboard's name is the `h1`; `h3` inside a story, where the
   * story's name is the `h1` and its own headings are `h2`.
   *
   * It was `h3` everywhere, which skipped a level on the dashboard page — a
   * reader moving by headings was told there was a missing level between the
   * dashboard and its first figure.
   */
  titleAs?: 'h2' | 'h3';
}) {
  const [showTable, setShowTable] = useState(false);

  if (block.type === 'text') {
    return (
      <div className="sc-block sc-block--text">
        <Title className="sc-block__title">{block.title}</Title>
        <p className="sc-block__prose">{block.display.text}</p>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="sc-block sc-block--loading" aria-busy="true">
        <Title className="sc-block__title">{block.title}</Title>
        <p className="sc-block__reading">Running…</p>
      </div>
    );
  }

  if (state.error || !state.result) {
    // Withheld and broken are different things, and a reader deserves to know
    // which one they are looking at (Phase 15).
    const guarded = isGuarded(state.error);
    return (
      <div className="sc-block">
        <Title className="sc-block__title">{block.title}</Title>
        <Alert
          tone={guarded ? 'info' : 'warning'}
          title={guarded ? 'Not shown here' : 'This block could not be drawn'}
        >
          {state.error?.message ?? 'No result was returned.'}
        </Alert>
      </div>
    );
  }

  if (block.type === 'kpi' || block.type === 'statistic') {
    return (
      <div className="sc-block sc-block--kpi">
        <KpiBlock block={block} result={state.result} />
      </div>
    );
  }

  return (
    <div className="sc-block">
      <Title className="sc-block__title">{block.title}</Title>
      {block.description && <p className="sc-block__description">{block.description}</p>}

      {block.type === 'table' ? (
        <ResultTable result={state.result} caption={block.title} />
      ) : (
        <>
          <Figure block={block} result={state.result} />
          <details
            className="sc-block__table"
            open={showTable}
            onToggle={(event) => setShowTable((event.target as HTMLDetailsElement).open)}
          >
            <summary>View as table</summary>
            <ResultTable result={state.result} caption={`${block.title}, as a table`} />
          </details>
        </>
      )}

      {state.result.is_derived && (
        <p className="sc-block__reading sc-block__derived">
          Summarised from data that is not public. Groups too small to be anonymous are not shown.
        </p>
      )}

      {state.result.warnings.map((warning) => (
        <p key={warning} className="sc-block__reading">
          {warning}
        </p>
      ))}
    </div>
  );
}
